import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { QrCode, Gift, Wallet, Star, ArrowRight, Sparkle } from '@phosphor-icons/react';

const steps = [
  {
    icon: QrCode,
    title: "Scannez",
    subtitle: "C'est simple",
    description: "À chaque visite chez un marchand partenaire, scannez le QR code à la caisse. C'est tout !",
    tip: "Pas besoin de carte physique",
    color: "#C3A365"
  },
  {
    icon: Sparkle,
    title: "Gagnez",
    subtitle: "Automatiquement",
    description: "Chaque scan vous rapporte des points. Plus vous visitez, plus vous gagnez. 1 visite = 10 points minimum.",
    tip: "100 points = £1 de valeur",
    color: "#145A3A"
  },
  {
    icon: Gift,
    title: "Offrez",
    subtitle: "À vos proches",
    description: "Achetez des cartes cadeaux utilisables dans tout le réseau. Parfait pour faire plaisir !",
    tip: "Valable chez tous les marchands",
    color: "#C3A365"
  },
  {
    icon: Wallet,
    title: "Profitez",
    subtitle: "De vos récompenses",
    description: "Convertissez vos points en crédit ou débloquez des récompenses exclusives chez vos marchands favoris.",
    tip: "Bronze → Silver → Gold",
    color: "#145A3A"
  }
];

export const OnboardingFlow = ({ onComplete }) => {
  const [current, setCurrent] = useState(0);
  const navigate = useNavigate();

  const next = () => {
    if (current < steps.length - 1) {
      setCurrent(current + 1);
    } else {
      localStorage.setItem('onboarding-completed', 'true');
      onComplete?.();
    }
  };

  const skip = () => {
    localStorage.setItem('onboarding-completed', 'true');
    onComplete?.();
  };

  const step = steps[current];
  const Icon = step.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-[#18261E] z-[10000] flex flex-col"
    >
      {/* Skip */}
      <div className="flex justify-end p-6">
        <button onClick={skip} className="text-[#8BA89A] text-sm">
          Passer
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="text-center"
          >
            {/* Step indicator */}
            <div className="text-[#8BA89A] text-xs tracking-widest uppercase mb-8">
              Étape {current + 1} sur {steps.length}
            </div>

            {/* Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="w-24 h-24 rounded-2xl mx-auto mb-8 flex items-center justify-center"
              style={{ backgroundColor: `${step.color}20`, border: `2px solid ${step.color}40` }}
            >
              <Icon size={48} style={{ color: step.color }} weight="duotone" />
            </motion.div>

            {/* Title */}
            <h1 className="text-4xl text-white font-light mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {step.title}
            </h1>
            <p className="text-[#C3A365] text-lg mb-6">{step.subtitle}</p>

            {/* Description */}
            <p className="text-[#8BA89A] text-base leading-relaxed mb-6 max-w-xs mx-auto">
              {step.description}
            </p>

            {/* Tip */}
            <div className="inline-flex items-center gap-2 bg-[#1E2F26] px-4 py-2 rounded-full">
              <Sparkle size={14} className="text-[#C3A365]" weight="fill" />
              <span className="text-[#C3A365] text-sm">{step.tip}</span>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom */}
      <div className="p-6">
        {/* Progress */}
        <div className="flex gap-2 justify-center mb-6">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all ${
                i === current ? 'w-8 bg-[#C3A365]' : i < current ? 'w-2 bg-[#C3A365]/50' : 'w-2 bg-[#3E5248]'
              }`}
            />
          ))}
        </div>

        {/* Button */}
        <button
          onClick={next}
          className="w-full bg-[#C3A365] text-[#18261E] py-4 rounded-xl font-semibold flex items-center justify-center gap-2"
        >
          {current === steps.length - 1 ? "C'est parti !" : 'Suivant'}
          <ArrowRight size={18} weight="bold" />
        </button>
      </div>
    </motion.div>
  );
};

export default OnboardingFlow;
